// src/templateManager/components/builders/RiskAreaBuilder.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Button,
  Group
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import SourceBuilder from './SourceBuilder';

/**
 * RiskAreaBuilder - Manages risk sources within a risk area
 * Each source can have type (text/multiselect), options, description
 */
function RiskAreaBuilder({ areaKey }) {
  const { control } = useFormContext();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `taxonomy.riskAreas.${areaKey}.riskSources`
  });

  // Add new source
  const addSource = () => {
    const newSource = {
      name: '',
      type: 'multiselect',
      value: [],
      options: [],
      description: '',
      causes: {}
    };
    append(newSource);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="md" fw={600}>Risk Sources</Text>
        <Text size="sm" c="dimmed">{fields.length} sources</Text>
      </Group>

      {fields.length === 0 ? (
        <Card withBorder p="md">
          <Text size="sm" c="dimmed" ta="center">
            No risk sources configured. Add sources that users can select during risk assessment.
          </Text>
        </Card>
      ) : (
        <Stack gap="sm">
          {fields.map((field, index) => (
            <SourceBuilder
              key={field.id}
              areaKey={areaKey}
              index={index}
              onRemove={() => remove(index)}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
            />
          ))}
        </Stack>
      )}

      {/* Add Source Button */}
      <Button
        variant="light"
        size="sm"
        leftSection={<IconPlus size={16} />}
        onClick={addSource}
        fullWidth
      >
        Add Risk Source
      </Button>
    </Stack>
  );
}

RiskAreaBuilder.propTypes = {
  areaKey: PropTypes.string.isRequired
};

export default RiskAreaBuilder;
